// Build two offers in sequence: target the configured reduction first, then 30% below the selected offer. Prefer a qualifying preset; otherwise generate a clean rounded amount.
const DEFAULT_UPSELL_RULES = {
  minAmount: 25,
  maxAmount: 1000,
  percentage: -70,
  reductionTolerance: 10
};

const MIN_UPSELL_PERCENTAGE = -70;
const MAX_UPSELL_PERCENTAGE = -60;
const SECOND_OFFER_PERCENTAGE = -30;
const decisionCache = new Map();

export function getUpsellDecision({ settings, orgUpsellSettings, donation, campaignId, campaignCurrency }) {
  const amount = Number(donation?.amount);
  const rules = normalizeRules({
    ...orgUpsellSettings,
    minAmount: settings?.upsellMinAmount ?? orgUpsellSettings?.minAmount,
    maxAmount: settings?.upsellMaxAmount ?? orgUpsellSettings?.maxAmount
  });
  const monthlyAmounts = getMonthlyAmounts(settings, donation?.currency, campaignCurrency);
  const cacheKey = [
    campaignId || 'default',
    donation?.currency || '',
    donation?.frequency || '',
    amount,
    rules.minAmount,
    rules.maxAmount,
    rules.percentage,
    rules.reductionTolerance,
    JSON.stringify(monthlyAmounts)
  ].join('|');

  if (decisionCache.has(cacheKey)) {
    return decisionCache.get(cacheKey);
  }

  const decision = buildUpsellDecision({ settings, rules, amount, frequency: donation?.frequency, monthlyAmounts });
  decisionCache.set(cacheKey, decision);
  return decision;
}

export function buildUpsellDecision({ settings, rules = DEFAULT_UPSELL_RULES, amount, frequency, monthlyAmounts = null }) {
  if (!settings?.upsellEnabled || settings.upsellMode !== 'auto') {
    return null;
  }

  if (settings.checkoutMode === 'items') {
    return null;
  }

  if (frequency !== 'single') {
    return null;
  }

  const oneTimeAmount = Number(amount);
  if (!Number.isFinite(oneTimeAmount) || oneTimeAmount <= 0) {
    return null;
  }

  const normalizedRules = normalizeRules(rules);
  if (oneTimeAmount < normalizedRules.minAmount || oneTimeAmount > normalizedRules.maxAmount) {
    return null;
  }

  if (!settings.frequenciesEnabled?.includes('monthly')) {
    return null;
  }

  monthlyAmounts = monthlyAmounts || normalizeMonthlyAmounts(settings.suggestedAmounts?.monthly);
  const firstOffer = buildOffer({
    baseAmount: oneTimeAmount,
    percentage: normalizedRules.percentage,
    reductionTolerance: normalizedRules.reductionTolerance,
    monthlyAmounts
  });
  if (!firstOffer) {
    return null;
  }

  const secondOffer = buildOffer({
    baseAmount: firstOffer.amount,
    percentage: SECOND_OFFER_PERCENTAGE,
    reductionTolerance: normalizedRules.reductionTolerance,
    monthlyAmounts,
    excludedAmounts: [firstOffer.amount]
  });
  if (!secondOffer) {
    return null;
  }

  const offers = [firstOffer, secondOffer];

  return {
    mode: 'auto',
    originalFrequency: 'single',
    originalAmount: oneTimeAmount,
    selectedFrequency: 'monthly',
    offeredAmounts: offers.map((offer) => offer.amount),
    offeredAmountSources: offers.map((offer) => offer.source),
    offeredAmountDescriptions: offers.map((offer) => offer.description || ''),
    percentage: normalizedRules.percentage,
    reductionTolerance: normalizedRules.reductionTolerance
  };
}

function buildOffer({ baseAmount, percentage, reductionTolerance, monthlyAmounts, excludedAmounts = [] }) {
  const usedAmounts = new Set(excludedAmounts);
  const presetOffer = normalizeMonthlyPresets(monthlyAmounts)
    .filter((preset) => preset.amount < baseAmount && !usedAmounts.has(preset.amount))
    .map((preset) => ({
      ...preset,
      reductionDifference: Math.abs((preset.amount / baseAmount - 1) * 100 - percentage),
      source: 'preset'
    }))
    .filter((offer) => offer.reductionDifference <= reductionTolerance)
    .sort((a, b) => a.reductionDifference - b.reductionDifference || b.amount - a.amount)[0];

  if (presetOffer) {
    return presetOffer;
  }

  const targetAmount = baseAmount * (1 + percentage / 100);
  const step = getRoundingStep(targetAmount);
  let amount = Math.round(targetAmount / step) * step;

  if (amount >= baseAmount) {
    amount = Math.floor((baseAmount - Number.EPSILON) / step) * step;
  }

  while (amount > 0) {
    if (amount < baseAmount && !usedAmounts.has(amount)) {
      return { amount, source: 'generated' };
    }
    amount -= step;
  }

  return null;
}

function getRoundingStep(amount) {
  if (amount >= 1000) {
    return 100;
  }
  if (amount >= 100) {
    return 50;
  }
  if (amount >= 10) {
    return 5;
  }
  return 1;
}

export function normalizeMonthlyAmounts(amounts) {
  const normalizedAmounts = (amounts || [])
    .map((amount) => Number(typeof amount === 'number' ? amount : amount?.amount))
    .filter((amount) => Number.isFinite(amount) && amount > 0);

  return [...new Set(normalizedAmounts)].sort((a, b) => b - a);
}

function getMonthlyAmounts(settings, currency, campaignCurrency) {
  const monthlyAmounts = settings?.suggestedAmounts?.monthly;
  if (!settings?.suggestedAmountsAutoConversion || !settings?.conversionRates || !currency) {
    return normalizeMonthlyPresets(monthlyAmounts);
  }

  const baseCurrency = settings.currency || settings.campaignCurrency || campaignCurrency;
  if (!baseCurrency || currency === baseCurrency) {
    return normalizeMonthlyPresets(monthlyAmounts);
  }

  const exchangeRateObj = Array.isArray(settings.conversionRates)
    ? settings.conversionRates.find((rate) => rate.fromCurrency === baseCurrency && rate.toCurrency === currency)
    : null;

  if (!exchangeRateObj?.rate) {
    return normalizeMonthlyPresets(monthlyAmounts);
  }

  const conversionRate = Number(exchangeRateObj.rate);
  if (!Number.isFinite(conversionRate)) {
    return normalizeMonthlyPresets(monthlyAmounts);
  }

  return normalizeMonthlyPresets(monthlyAmounts).map((preset) => ({
    ...preset,
    amount: Math.round(preset.amount * conversionRate)
  }));
}

function normalizeMonthlyPresets(amounts) {
  const presets = (amounts || [])
    .map((item) => ({
      amount: Number(typeof item === 'number' ? item : item?.amount),
      description: typeof item?.description === 'string' ? item.description : ''
    }))
    .filter((preset) => Number.isFinite(preset.amount) && preset.amount > 0)
    .sort((a, b) => b.amount - a.amount);

  return presets.filter((preset, index) => presets.findIndex((item) => item.amount === preset.amount) === index);
}

function normalizeRules(rules = {}) {
  const minAmount = Number(rules.minAmount ?? DEFAULT_UPSELL_RULES.minAmount);
  const maxAmount = Number(rules.maxAmount ?? DEFAULT_UPSELL_RULES.maxAmount);
  const percentage = Number(rules.percentage ?? DEFAULT_UPSELL_RULES.percentage);
  const reductionTolerance = Number(rules.reductionTolerance ?? DEFAULT_UPSELL_RULES.reductionTolerance);
  const normalizedPercentage = Number.isFinite(percentage)
    ? Math.min(MAX_UPSELL_PERCENTAGE, Math.max(MIN_UPSELL_PERCENTAGE, percentage))
    : DEFAULT_UPSELL_RULES.percentage;

  return {
    minAmount: Number.isFinite(minAmount) ? minAmount : DEFAULT_UPSELL_RULES.minAmount,
    maxAmount: Number.isFinite(maxAmount) ? maxAmount : DEFAULT_UPSELL_RULES.maxAmount,
    percentage: normalizedPercentage,
    reductionTolerance: Number.isFinite(reductionTolerance) ? Math.min(100, Math.max(0, reductionTolerance)) : DEFAULT_UPSELL_RULES.reductionTolerance
  };
}
